import { getFormattedUnit } from 'config';
import React, { useEffect, useRef, useState } from 'react';
import ReactHtmlParser from "react-html-parser";
import LastRateHistoryModal from './LastRateHistoryModal';
import { findQuotationLineItem } from './quotationLineHelpers';
import StaticPopupNew from 'components/StaticPopupNew';


const MaterialRFQTable = ({ rfqData, sortedSuppliers, withGst, handleMaterialDropdown, onTotalLowestPriceSum, isAdvancedTransport, selectedMaterialsMap, hasBudgetColumns = true, hiddenLineItemKeys = new Set(), getLineItemKey, renderLineItemHideButton }) => {

    const [openDropdown, setOpenDropdown] = useState({ rowIndex: null, vendorIndex: null });
    const [historyMaterial, setHistoryMaterial] = useState(null);
    const [brandPopupMaterial, setBrandPopupMaterial] = useState(null);
    const [openRevisionTrail, setOpenRevisionTrail] = useState(() => new Set());
    const vendorCellRefs = useRef({});
    const showSelectedValueColumn = false;

    // Calculate lowest prices per material based on material._id
    // Calculate lowest prices per material based on material._id
    const dropdownRef = useRef(null);
    const lowestPrices = {};
    let totalLowestPriceSum = 0;

    const findVendorMaterialItem = (materialDetails, material, rowIndex) =>
        findQuotationLineItem(materialDetails, material, rowIndex);

    const getDiscountedRate = (item) => {
        if (!item) return 0;
        const basePrice = Number(item?.basePrice);
        const discount = Number(item?.discount || 0);

        if (!Number.isFinite(basePrice)) return 0;

        return basePrice - (basePrice * discount) / 100;
    };

    const getRevisionRemarkText = (value) =>
        String(value || "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim();

    const formatRevisionAmount = (value) =>
        new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value) || 0);

    const formatRevisionDiscount = (value) =>
        `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value) || 0)}%`;

    const formatRevisionDifference = (difference, differencePercent) => {
        if (difference === null || difference === undefined) return "-";

        const numericDifference = Number(difference);
        if (!Number.isFinite(numericDifference)) return "-";

        return (
            <>
                {formatRevisionAmount(Math.abs(numericDifference))}{" "}
                {numericDifference < 0 && "\u25BC"}
                {numericDifference > 0 && "\u25B2"}
                {differencePercent !== null && differencePercent !== undefined && (
                    <span className="ml-1 text-[10px]">({Number(differencePercent).toFixed(2)}%)</span>
                )}
            </>
        );
    };

    const formatRevisionDate = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
    };

    const getMaterialRevisionSignature = (item) => {
        const baseRate = Number(item?.basePrice);
        const discount = Number(item?.discount || 0);
        return JSON.stringify({
            baseRate: Number.isFinite(baseRate) ? baseRate : 0,
            discount: Number.isFinite(discount) ? discount : 0,
            gst: Number(item?.gst || 0) || 0,
            netRate: getDiscountedRate(item),
            quotedRate: Number(item?.quotedRate ?? item?.quotedValue ?? item?.priceWithoutGst ?? item?.netPrice ?? 0) || 0,
            transportRate: Number(item?.transportRate || 0) || 0,
            transportCost: Number(item?.transportCost || 0) || 0,
            isAvailable: item?.isAvailable ?? true,
            deliveryDate: item?.deliveryDate || "",
            remark: getRevisionRemarkText(item?.remark),
        });
    };

    const getMaterialRevisionTrail = (supplier, material, rowIndex) => {
        let previousSignature = null;

        return (supplier?.vendorQuotations?.quotationSets || [])
            .reduce((trail, quotationSet) => {
                const item = findVendorMaterialItem(quotationSet?.materialDetails, material, rowIndex);
                if (!item) return trail;

                const signature = getMaterialRevisionSignature(item);
                if (trail.length > 0 && signature === previousSignature) return trail;

                const baseRate = Number(item?.basePrice);
                const discount = Number(item?.discount || 0);
                const revisionIndex = trail.length;

                trail.push({
                    label: `R${revisionIndex}`,
                    date: formatRevisionDate(quotationSet?.submittedAt || quotationSet?.updatedAt || quotationSet?.createdAt),
                    submittedBy: quotationSet?.submittedBy || quotationSet?.updatedBy || "-",
                    baseRate: Number.isFinite(baseRate) ? baseRate : 0,
                    discount: Number.isFinite(discount) ? discount : 0,
                    netRate: getDiscountedRate(item),
                    quotedRate: Number(item?.quotedRate ?? item?.quotedValue ?? item?.priceWithoutGst ?? item?.netPrice ?? 0) || 0,
                    remark: getRevisionRemarkText(item?.remark),
                });
                previousSignature = signature;

                return trail;
            }, [])
            .map((revision, revisionIndex, trail) => {
                const previousRevision = trail[revisionIndex - 1];
                const netRateDifference = previousRevision
                    ? revision.netRate - previousRevision.netRate
                    : null;
                const netRateDifferencePercent = previousRevision && previousRevision.netRate !== 0
                    ? (Math.abs(netRateDifference) / Math.abs(previousRevision.netRate)) * 100
                    : null;

                return {
                    ...revision,
                    label: `${revision.label}${revisionIndex === trail.length - 1 ? " (current)" : ""}`,
                    netRateDifference,
                    netRateDifferencePercent,
                };
            });
    };

    const toggleRevisionTrail = (event, rowIndex, vendorIndex, trailLength) => {
        event.stopPropagation();
        if (trailLength <= 1) return;

        const key = `${rowIndex}-${vendorIndex}`;
        setOpenRevisionTrail((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    rfqData?.materials?.forEach((material, rowIndex) => {
        let minPrice = Infinity;
        let minSupplierIndexes = [];

        sortedSuppliers?.forEach((supplier, vendorIndex) => {
            const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
            const vendorItem = findVendorMaterialItem(vendorQuotation?.materialDetails, material, rowIndex);

            if (vendorItem && vendorItem.basePrice > 0) {  // Ignore basePrice === 0
                // Calculate the discounted price
                const discountedPrice = vendorItem.basePrice - (vendorItem.basePrice * vendorItem.discount) / 100;

                // Ignore discountedPrice === 0
                if (discountedPrice > 0) {
                    if (discountedPrice < minPrice) {
                        minPrice = discountedPrice;
                        minSupplierIndexes = [vendorIndex]; // Reset and store new lowest index
                    } else if (discountedPrice === minPrice) {
                        minSupplierIndexes.push(vendorIndex); // Add vendor to lowest list
                    }
                }
            }
        });

        // If no valid prices were found, default to 0
        const lowestPrice = minPrice === Infinity ? 0 : minPrice;

        // Add the lowest price for the current material to the total sum
        const materialTotalCost = lowestPrice * material.quantity;
        totalLowestPriceSum += materialTotalCost;

        // If no valid prices were found, default to 0
        lowestPrices[material._id] = {
            minPrice: minPrice === Infinity ? 0 : minPrice,
            minSupplierIndexes
        };
    });

    onTotalLowestPriceSum(totalLowestPriceSum);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setOpenDropdown({ rowIndex: null, vendorIndex: null }); // close
            }
        }

        if (openDropdown.rowIndex !== null && openDropdown.vendorIndex !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);

    const calculatePercentageDifference = (firstPrice, latestPrice) => {
        if (!firstPrice || firstPrice === 0) return 0;
        return latestPrice - firstPrice;
    };

    const calculateDifferencePercent = (firstPrice, difference) => {
        const numericFirstPrice = Number(firstPrice);
        const numericDifference = Number(difference);

        if (!Number.isFinite(numericFirstPrice) || numericFirstPrice === 0 || !Number.isFinite(numericDifference)) {
            return null;
        }

        return (Math.abs(numericDifference) / Math.abs(numericFirstPrice)) * 100;
    };

    const getCleanText = (value) =>
        String(value || "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim();

    const getMaterialBrands = (material) => {
        const rawBrands = material?.brands || material?.brand || material?.approvedBrands || [];
        const brands = Array.isArray(rawBrands) ? rawBrands : String(rawBrands).split(",");

        return brands
            .map((brand) => {
                if (typeof brand === "string") return brand.trim();
                return String(brand?.name || brand?.brandName || brand?.label || brand?.value || "").trim();
            })
            .filter(Boolean);
    };

    const renderMaterialBrands = (material) => {
        const brands = getMaterialBrands(material);
        if (brands.length === 0) return null;

        const visibleBrands = brands.slice(0, 2);
        const hiddenCount = brands.length - visibleBrands.length;

        return (
            <div className="mt-1 flex flex-wrap items-center gap-1">
                {visibleBrands.map((brand, index) => (
                    <span
                        key={`${brand}-${index}`}
                        className="truncate text-[10px] font-medium"
                        title={brand}
                    >
                        {brand},
                    </span>
                ))}
                {hiddenCount > 0 && (
                    <button
                        type="button"
                        className="text-[10px] font-semibold text-blue-700 hover:bg-blue-50 underline"
                        onClick={(event) => {
                            event.stopPropagation();
                            setBrandPopupMaterial(material);
                        }}
                    >
                        +{hiddenCount} more
                    </button>
                )}
            </div>
        );
    };


    return (
        <>
            {rfqData?.materials?.map((material, rowIndex) => {
                const lineItemKey = getLineItemKey?.("material", material, rowIndex) ?? `material:${material?._id ?? material?.id ?? rowIndex}`;
                if (hiddenLineItemKeys.has(lineItemKey)) return null;

                const hasAnyRemark = sortedSuppliers?.some((supplier) => {
                    const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                    const vendorItem = findVendorMaterialItem(vendorQuotation?.materialDetails, material, rowIndex);
                    return Boolean(getCleanText(vendorItem?.remark));
                });

                return (
                <React.Fragment key={material._id || rowIndex}>
                <tr className='bg-white text-xs'>
                    <div className='sticky left-0 z-10 h-full bg-white' style={{ minWidth: 480, maxWidth: 480, width: 480, boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                        <td className="grid grid-cols-18 gap-x-1 bg-white sticky py-2 left-0 px-[22px] h-full border-b w-full">
                            <div className="col-span-1 flex flex-col items-center gap-1">
                                <span>{rowIndex + 1}.</span>
                                {renderLineItemHideButton?.(
                                    lineItemKey,
                                    getCleanText(material?.name) || `Material ${rowIndex + 1}`
                                )}
                            </div>
                            <div className='flex-col bg-white justify-between' style={{ gridColumn: `span ${hasBudgetColumns ? 3 : 9} / span ${hasBudgetColumns ? 3 : 9}` }}>
                                <p className='flex'>
                                    <span className='w-full'>
                                        {ReactHtmlParser(material?.name)}
                                        {(material?.code || material?.materialCode || material?.itemCode) && (
                                            <span className='mt-0.5 block text-[10px] font-medium text-[#6B7280]'>
                                               ( {material?.code || material?.materialCode || material?.itemCode})
                                            </span>
                                        )}
                                    </span>
                                </p>
                                {renderMaterialBrands(material)}
                            </div>
                            <p className='col-span-2 min-w-0 whitespace-normal' style={{ overflowWrap: 'anywhere' }}> {material?.quantity} {getFormattedUnit(material?.unit)}</p>
                            <button
                                className="col-span-3 flex items-start justify-end gap-1 bg-white text-right text-xs text-blue-700 disabled:cursor-default disabled:text-gray-500"
                                disabled={material?.lastRate === undefined || material?.lastRate === null}
                                onClick={() => setHistoryMaterial(material)}
                                title={material?.lastRate !== undefined ? "View last rate history" : "No historical rate found"}
                                type="button"
                            >
                                <span className={material?.lastRate !== undefined ? "hover:underline" : ""}>
                                    {material?.lastRate !== undefined ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(material.lastRate) : '-'}
                                </span>
                                {Array.isArray(material?.lastRateHistory) && material.lastRateHistory.length > 1 && (
                                    <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">
                                        +{material.lastRateHistory.length - 1}
                                    </span>
                                )}
                            </button>
                            {hasBudgetColumns && (
                                <p className="text-xs pl-1 text-right col-span-3">
                                {material?.budget > 0
                                    ? new Intl.NumberFormat('en-IN', {
                                        maximumFractionDigits: 2,
                                    }).format(material?.budget)
                                    : '-'}
                                </p>
                            )}

                            {hasBudgetColumns && (
                                <p className="text-xs pl-1 text-right col-span-3">
                                {material?.budgetAmount > 0
                                    ? new Intl.NumberFormat('en-IN', {
                                        maximumFractionDigits: 2,
                                    }).format(material?.budgetAmount)
                                    : '-'}
                                </p>
                            )}

                            <p
                                className="col-span-3 text-xs text-right text-blue-700 bg-white break-words cursor-pointer hover:underline"
                                onClick={() => {
                                    const lowestVendorIndex =
                                        lowestPrices[material._id]?.minSupplierIndexes?.[0];

                                    if (lowestVendorIndex === undefined) return;

                                    const cell = vendorCellRefs.current[`${rowIndex}-${lowestVendorIndex}`];
                                    if (!cell) return;

                                    // Find horizontal scroll container
                                    const scrollContainer = cell.closest(".comparison-scrollbar");
                                    if (!scrollContainer) return;

                                    // Sticky column width (left column)
                                    const stickyColumn = scrollContainer.querySelector(".fixed-column");
                                    const stickyWidth = stickyColumn?.offsetWidth || 420;

                                    // Cell position relative to scroll container
                                    const cellLeft =
                                        cell.getBoundingClientRect().left -
                                        scrollContainer.getBoundingClientRect().left;

                                    // Final scroll position
                                    const targetScrollLeft =
                                        scrollContainer.scrollLeft + cellLeft - stickyWidth;

                                    scrollContainer.scrollTo({
                                        left: targetScrollLeft,
                                        behavior: "smooth",
                                    });
                                }}

                            >
                                {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 })
                                    .format(lowestPrices[material._id]?.minPrice ?? 0)}
                            </p>
                        </td>
                    </div>

                    {sortedSuppliers?.map((supplier, vendorIndex) => {
                        const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                        const firstVendorQuotation = supplier.vendorQuotations?.quotationSets?.[0];

                        const vendorItem = findVendorMaterialItem(vendorQuotation?.materialDetails, material, rowIndex) || {};
                        const firstVendorItem = findVendorMaterialItem(firstVendorQuotation?.materialDetails, material, rowIndex) || {};

                        const supplierPrice = vendorItem.basePrice - (vendorItem.basePrice * vendorItem.discount) / 100 ?? 0;
                        const firstSupplierPrice = firstVendorItem.basePrice - (firstVendorItem.basePrice * firstVendorItem.discount) / 100 ?? 0;


                        const isLowest = lowestPrices[material._id]?.minSupplierIndexes.includes(vendorIndex);
                        const percentageDifference = calculatePercentageDifference(firstSupplierPrice, supplierPrice);
                        const differencePercent = calculateDifferencePercent(firstSupplierPrice, percentageDifference);
                        const revisionTrail = getMaterialRevisionTrail(supplier, material, rowIndex);
                        const revisionCount = Math.max(revisionTrail.length - 1, 0);
                        const isRevisionTrailOpen = openRevisionTrail.has(`${rowIndex}-${vendorIndex}`);

                        return (
                            <td
                                ref={(el) => {
                                    if (el) {
                                        vendorCellRefs.current[`${rowIndex}-${vendorIndex}`] = el;
                                    }
                                }}
                                key={vendorIndex}
                                className={`max-w-[420px] min-w-[420px] pt-1 px-4 z-0 align-top border border-gray-300}`}
                            >
                                <div onClick={() => {
                                    /*
                                     * Legacy material-details dropdown trigger disabled.
                                     * Restore the setOpenDropdown block when this dropdown is needed again.
                                     *
                                     * setOpenDropdown((prev) => {
                                     *     if (prev.rowIndex === rowIndex && prev.vendorIndex === vendorIndex) {
                                     *         return { rowIndex: null, vendorIndex: null };
                                     *     }
                                     *     return { rowIndex, vendorIndex };
                                     * });
                                     */
                                }} className='relative py-1'>
                                    <div className='w-full'>
                                        {vendorItem.deliveryDate && rfqData.providingDate && (
                                            new Date(vendorItem.deliveryDate).getTime() !== new Date(rfqData.providingDate).getTime() && (
                                                <p className='text-xs text-[#DC7604]'>
                                                    Proposed: {new Date(vendorItem.deliveryDate).toLocaleDateString('en-GB')}
                                                </p>
                                            )
                                        )}
                                        <div className='flex justify-between items-center space-x-2'></div>
                                    </div>

                                    <div className='grid grid-cols-18 items-start w-full h-full gap-x-1'>
                                        {/* Column 1: Price, GST, Discount, Attachments */}
                                        <div className={`${showSelectedValueColumn ? 'col-span-4' : 'col-span-5'}`}>
                                            {'isAvailable' in vendorItem && !vendorItem.isAvailable ? (
                                                <p className='text-xs align-text-top h-full'>NA</p>
                                            ) : (
                                                <>
                                                    <p className={`text-xs ${isLowest ? 'bg-lime-200 px-1 w-fit' : ''}`}>
                                                        {isNaN(supplierPrice) || supplierPrice == null
                                                            ? '0.00'
                                                            : new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(supplierPrice)}
                                                    </p>
                                                    <div className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
                                                        <p className='pt-2 text-xs text-[#777777]'>
                                                            Disc.: {vendorItem?.discount != null
                                                                ? `${Math.floor(parseFloat(vendorItem.discount) * 100) / 100}%`
                                                                : '0%'}
                                                        </p>
                                                        {vendorItem?.gst != null && vendorItem.gst !== '' && (
                                                            <p className='pt-2 text-xs text-[#777777] whitespace-nowrap'>GST: {vendorItem.gst}%</p>
                                                        )}
                                                        {vendorItem?.attachments?.length > 0 && (
                                                            <div className='relative mt-1'>
                                                                <img src="https://storagereponeevaydevcdn.blob.core.windows.net/business/files.svg" alt="" className='w-3 h-3' />
                                                                <div className='bg-[#f05252] rounded-full -top-2 -right-2 h-3 w-3 flex justify-center items-center absolute'>
                                                                    <p className='text-[10px] text-white text-center pt-[3px]'>{vendorItem?.attachments?.length}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Column 2: First Supplier Price */}
                                        <p className={`text-xs text-right ${showSelectedValueColumn ? 'col-span-3' : 'col-span-4'}`}>
                                            {'isAvailable' in vendorItem && !vendorItem.isAvailable
                                                ? 'NA'
                                                : (firstSupplierPrice > 0
                                                    ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(firstSupplierPrice)
                                                    : '-')}
                                        </p>

                                        {/* Column 3: Percentage Difference */}
                                        <div
                                            className={`relative text-xs text-right flex flex-col items-end ${showSelectedValueColumn ? 'col-span-3' : 'col-span-4'} ${'isAvailable' in vendorItem && !vendorItem.isAvailable
                                                ? 'text-[#777777]'
                                                : parseFloat(percentageDifference) < 0
                                                    ? 'text-green-600'
                                                    : parseFloat(percentageDifference) > 0
                                                        ? 'text-red-600'
                                                        : 'text-[#777777]'
                                                }`}
                                        >
                                            {'isAvailable' in vendorItem && !vendorItem.isAvailable ? (
                                                'NA'
                                            ) : (
                                                <div className="flex flex-col items-end gap-1">
                                                    <div className={`flex flex-col items-end ${revisionTrail.length > 1 ? "cursor-pointer rounded px-1 hover:bg-gray-50" : ""}`}>
                                                        <span>
                                                            {Math.abs(parseFloat(percentageDifference)).toFixed(2)}{' '}
                                                            {parseFloat(percentageDifference) < 0 && '\u25BC'}
                                                            {parseFloat(percentageDifference) > 0 && '\u25B2'}
                                                        </span>
                                                        <span className="text-[10px] leading-3">
                                                            {differencePercent === null ? '-' : '(' + differencePercent.toFixed(2) + '%)'}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Column 4: Final Net/Non-GST Price */}
                                        <div className={`flex flex-col items-end text-right ${showSelectedValueColumn ? 'col-span-4' : 'col-span-5'}`}>
                                            <p className='text-xs text-right w-full font-normal text-[#2D2D11]'>
                                                {'isAvailable' in vendorItem && !vendorItem.isAvailable
                                                    ? 'NA'
                                                    : `${withGst
                                                        ? new Intl.NumberFormat('en-IN', {
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 0,
                                                        }).format(vendorItem.netPrice ?? 0)
                                                        : new Intl.NumberFormat('en-IN', {
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 0,
                                                        }).format(vendorItem?.priceWithoutGst ?? 0)
                                                    }`}
                                            </p>
                                            {revisionTrail.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="mt-[2px] inline-flex items-center justify-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-[1px] text-[10px] font-semibold leading-4 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                                                    onClick={(event) => toggleRevisionTrail(event, rowIndex, vendorIndex, revisionTrail.length)}
                                                >
                                                    {isRevisionTrailOpen
                                                        ? "Hide revisions"
                                                        : `${revisionCount} ${revisionCount === 1 ? "revision" : "revisions"}`}
                                                </button>
                                            )}
                                        </div>

                                    </div>
                                    {isRevisionTrailOpen && (
                                        <div
                                            className="mt-2 w-[calc(100%_+_24px)] min-w-[520px] rounded-xl border border-blue-100 bg-[#F8FAFC] px-3 py-3 text-left text-[11px] text-[#3F4B64] shadow-lg"
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            <div className="grid grid-cols-[38px_108px_56px_42px_58px_64px_minmax(95px,1fr)] gap-x-2 border-b border-gray-400 pb-2 text-[11px] font-semibold text-gray-500">
                                                <span>Round</span>
                                                <span className="text-center">Date</span>
                                                <span className="text-right">Rate</span>
                                                <span className="text-right">Disc.</span>
                                                <span className="text-right">Net rate</span>
                                                <span className="text-right">Difference</span>
                                                <span className="text-right">Amount</span>
                                            </div>
                                            {revisionTrail.map((revision, revisionIndex) => (
                                                <React.Fragment key={`${revision.label}-${revisionIndex}`}>
                                                    <div
                                                        className={`grid grid-cols-[38px_108px_56px_42px_58px_64px_minmax(95px,1fr)] gap-x-2 border-b ${revision.remark ? "border-gray-200" : "border-gray-300"} py-2`}
                                                    >
                                                        <span className={`font-semibold ${revisionIndex === revisionTrail.length - 1 ? "text-blue-700" : "text-gray-600"}`}>{revision.label}</span>
                                                        <span className="whitespace-normal break-words text-center leading-4">
                                                            <span className="block">{revision.submittedBy}</span>
                                                            <span className="block">{revision.date}</span>
                                                        </span>
                                                        <span className="text-right font-medium text-[#2D2D11]">{formatRevisionAmount(revision.baseRate)}</span>
                                                        <span className="text-right font-medium text-[#2D2D11]">{formatRevisionDiscount(revision.discount)}</span>
                                                        <span className={`text-right font-semibold ${revisionIndex === revisionTrail.length - 1 ? "text-blue-700" : "text-[#2D2D11]"}`}>
                                                            {formatRevisionAmount(revision.netRate)}
                                                        </span>
                                                        <span className={`text-right font-semibold ${revision.netRateDifference < 0 ? "text-emerald-600" : revision.netRateDifference > 0 ? "text-red-600" : "text-[#777777]"}`}>
                                                            {formatRevisionDifference(revision.netRateDifference, revision.netRateDifferencePercent)}
                                                        </span>
                                                        <span className="text-right font-medium text-[#2D2D11]">{formatRevisionAmount(revision.quotedRate)}</span>
                                                    </div>
                                                    {revision.remark && (
                                                        <div className="border-b border-gray-300 py-1 text-gray-600">
                                                            <span className="font-semibold">Remarks:</span>{" "}{revision.remark}
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    )}
                                </div>


                                {/* Legacy material-details dropdown retained for future use and disabled for now. */}
                                {false && (openDropdown.rowIndex === rowIndex && openDropdown.vendorIndex === vendorIndex) && (
                                    <div ref={dropdownRef} className='absolute w-[390px] z-10 bg-white shadow-md border border-[#FFA653] p-0 h-fit whitespace-nowrap'>
                                        <div className='p-2 space-y-2 text-xs w-full'>
                                            <div className='flex items-center justify-between'>
                                                <label>
                                                    MRP
                                                </label>
                                                <div className='relative max-w-3/4 ml-2 group'>
                                                    <p className='px-2 text-xs whitespace-normal break-words border-none truncate  bg-[#F0F0F0] text-[#404040] text-right cursor-default'>Rs. {new Intl.NumberFormat('en-IN').format(vendorItem.basePrice || 0)}</p>
                                                </div>
                                            </div>
                                            {isAdvancedTransport && <div className='flex items-center justify-between'>
                                                <label >
                                                    Transport Rate Per Unit
                                                </label>
                                                <div className='relative max-w-3/4 ml-2 group'>
                                                    <p className='px-2 text-xs whitespace-normal break-words border-none truncate  bg-[#F0F0F0] text-[#404040] text-right cursor-default'>Rs. {new Intl.NumberFormat('en-IN').format(vendorItem?.transportRate || 0)}</p>
                                                </div>
                                            </div>}
                                            <div className='flex items-center justify-between'>
                                                <label >
                                                    Brand
                                                </label>
                                                <div className='relative max-w-3/4 ml-2 group'>
                                                    <p className='px-2 text-xs whitespace-normal break-words border-none truncate  bg-[#F0F0F0] text-[#404040] text-right cursor-default'>{vendorItem.brand || "NA"}</p>
                                                    {/* {vendorItem.brand && (
                                                    <div className="absolute bottom-full break-words mb-1 hidden group-hover:flex px-2 py-1 bg-[#f0f0f0] text-[#404040] text-xs rounded shadow-lg">
                                                        {vendorItem.brand}
                                                    </div>
                                                )} */}
                                                </div>
                                            </div>
                                            <label className='flex items-center justify-between w-full'>
                                                <span>Quantity</span>
                                                <p className='px-2 text-xs border-none bg-[#F0F0F0] text-[#404040] text-right' >{vendorItem.quantity || "0"} </p>
                                            </label>
                                            <label className='flex items-center justify-between w-full'>
                                                <span>GST (%)</span>
                                                <p className='px-2 text-xs border-none bg-[#F0F0F0] text-[#404040] text-right'>{vendorItem.gst || "0"} </p>
                                            </label>
                                            <label className='flex items-center justify-between w-full'>
                                                <span>Discount (%)</span>
                                                <p className='px-2 text-xs border-none bg-[#F0F0F0] text-[#404040] text-right'>{vendorItem.discount || "0"} </p>
                                            </label>
                                            <label className='flex items-center justify-between w-full'>
                                                <span>Total (Rs.)</span>
                                                <p className='px-2 text-xs border-none bg-[#F0F0F0] text-[#404040] text-right'>{new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(vendorItem.netPrice ?? 0)} </p>
                                            </label>
                                        </div>
                                        <div className=' p-2'>
                                            <p className='text-[#434343]'>Remarks:</p>
                                            <p
                                                placeholder='Note'
                                                className='placeholder:italic break-words whitespace-normal max-h-[60px] overflow-y-auto text-xs cursor-default resize-none border-[#FFA653] border-dashed'
                                            >{ReactHtmlParser(vendorItem.remark) || "NA"}
                                            </p>
                                        </div>


                                        {vendorItem.attachments && vendorItem.attachments.length > 0 && (
                                            <div className="p-2">
                                                <h3 className="text-xs font-medium text-[#434343]">Attachments</h3>
                                                <div className="space-y-2">
                                                    {vendorItem.attachments.map((attachment, index) => {
                                                        const lastIndex = attachment.Name.lastIndexOf('.'); // Extract file extension
                                                        const path = lastIndex !== -1 ? attachment.Name.substring(lastIndex + 1) : null;
                                                        return (
                                                            <div key={index} className="flex justify-between items-center mt-2">
                                                                <div className="px-1 flex items-center justify-center border border-gray-300 rounded-full py-1 space-x-1 text-[#434343]">
                                                                    {/* Dynamic icon based on file type */}
                                                                    <img src={`/assets/icons/${path}.png`} alt={path} className="h-5 w-5" />
                                                                    {/* Document name now clickable */}
                                                                    <a
                                                                        href={attachment.Content}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        download={attachment.Name}
                                                                        className="text-xs truncate text-[#434343] hover:underline"
                                                                    >
                                                                        {attachment.Name}
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                                }
                            </td>
                        );
                    })}
                </tr>
                {hasAnyRemark && (
                    <tr className="bg-white text-xs">
                        <div className='sticky left-0 z-10 h-full bg-white' style={{ minWidth: 480, maxWidth: 480, width: 480, boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                            <td className="sticky left-0 h-full w-full bg-white px-[22px] py-2">
                                <p className="text-[11px] font-semibold italic text-[#6B7280]">Vendor Remarks</p>
                            </td>
                        </div>
                        {sortedSuppliers?.map((supplier, vendorIndex) => {
                            const vendorQuotation = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
                            const vendorItem = findVendorMaterialItem(vendorQuotation?.materialDetails, material, rowIndex);
                            const remark = getCleanText(vendorItem?.remark);

                            return (
                                <td
                                    key={`remark-${material._id || rowIndex}-${vendorIndex}`}
                                    className="max-w-[420px] min-w-[420px] border border-gray-300 px-4 py-2"
                                >
                                    {remark ? (
                                        <p className="whitespace-normal break-words text-[11px] italic leading-4 text-[#555555]">
                                            {ReactHtmlParser(vendorItem?.remark)}
                                        </p>
                                    ) : (
                                        <span className="text-[11px] text-gray-300">-</span>
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                )}
                </React.Fragment>
            );
            })}
            {historyMaterial && (
                <LastRateHistoryModal
                    material={historyMaterial}
                    onClose={() => setHistoryMaterial(null)}
                />
            )}
            {brandPopupMaterial && (
                <StaticPopupNew
                    openModal={Boolean(brandPopupMaterial)}
                    handleClose={() => setBrandPopupMaterial(null)}
                    header="Approved Brands"
                    width="md"
                    bodyStyle="p-4"
                >
                    <div className="flex flex-wrap gap-2">
                        {getMaterialBrands(brandPopupMaterial).map((brand, index) => (
                            <span
                                key={`${brand}-${index}`}
                                className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                            >
                                {brand}
                            </span>
                        ))}
                    </div>
                </StaticPopupNew>
            )}
        </>
    );
};

export default MaterialRFQTable;
